import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

export type UnitActionCardVariant = 'default' | 'sheet';

export interface UnitActionCardProps {
  /** Leading icon rendered inside the pastel tile */
  icon: React.ReactNode;
  /** Pastel background color of the icon tile */
  iconBackgroundColor: string;
  title: string;
  description: string;
  buttonTitle: string;
  onPress?: (() => void) | undefined;
  /** Button variant — default primary; use outline for Cancel */
  buttonVariant?: 'primary' | 'outline';
  /** When true, the action button is disabled (e.g. another ADHA reservation exists). */
  disabled?: boolean;
  /**
   * - default: desktop sidebar card
   * - sheet:   mobile bottom-sheet card (more compact, bordered)
   */
  variant?: UnitActionCardVariant;
}

/**
 * Sidebar / bottom-sheet action card (Book an Appointment / Reserve Unit):
 * pastel icon tile, title, description and a full-width primary button.
 */
export function UnitActionCard({
  icon,
  iconBackgroundColor,
  title,
  description,
  buttonTitle,
  onPress,
  buttonVariant = 'primary',
  disabled = false,
  variant = 'default',
}: Readonly<UnitActionCardProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const isSheet = variant === 'sheet';

  return (
    <View
      style={[
        styles.card,
        isSheet && styles.cardSheet,
        {
          backgroundColor: colors.backgroundLevel2,
          ...(isSheet ? { borderColor: colors.outlineDisabled } : null),
        },
      ]}
    >
      <View style={styles.headerRow}>
        <View
          style={[styles.iconTile, { backgroundColor: iconBackgroundColor }]}
        >
          {icon}
        </View>
        <View style={styles.headerText}>
          <Typography
            variant={isSheet ? 'headlineLarge' : 'title3'}
            color="primary"
            style={Platform.OS === 'web' && isRTL ? { textAlign: 'right' } : undefined}
          >
            {title}
          </Typography>
          <Typography
            variant={isSheet ? 'subhead' : 'body'}
            color="secondary"
            style={[
              isSheet ? styles.descriptionSheet : undefined,
              Platform.OS === 'web' && isRTL ? { textAlign: 'right' } : undefined
            ]}
          >
            {description}
          </Typography>
        </View>
      </View>
      <Button
        title={buttonTitle}
        variant={buttonVariant}
        fullWidth
        onPress={onPress}
        disabled={disabled}
        {...(buttonVariant === 'outline'
          ? { style: { backgroundColor: 'transparent' } }
          : {})}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 24,
  },
  cardSheet: {
    gap: 16,
    borderWidth: 0.5,
  },
  headerRow: {
    flexDirection: 'row',
    gap: 16,
  },
  iconTile: {
    padding: 16,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  descriptionSheet: {
    fontSize: 14,
    lineHeight: 21,
  },
});

export default UnitActionCard;
